/**
 * link-prefetch.js
 * Warm collection -> PDP navigation so the tap feels instant.
 *
 * Strategy (progressive enhancement, conservative on bandwidth):
 *   1. Speculation Rules API where supported: prefetch product URLs with
 *      "moderate" eagerness (fires on hover / pointerdown intent, ~200ms),
 *      so we never speculatively fetch the whole grid up front.
 *   2. Fallback for browsers without Speculation Rules: on hover / touchstart /
 *      keyboard focus of a product link, inject a one-shot <link rel="prefetch">.
 *
 * Guards: fully disabled when the user is on a data-saver connection
 * (navigator.connection.saveData) or has prefers-reduced-data set, and on
 * 2g/slow-2g effective connection types. No runaway prefetching on mobile.
 */
(function () {
  'use strict';

  // --- Bandwidth / data-saver guards --------------------------------------
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn) {
    if (conn.saveData) return;
    if (/(^|-)2g$/.test(conn.effectiveType || '')) return;
  }
  try {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)').matches) return;
  } catch (e) { /* older browsers: ignore */ }

  // Only prefetch same-origin product detail pages.
  function isProductLink(a) {
    if (!a || a.tagName !== 'A' || !a.href) return false;
    if (a.origin !== window.location.origin) return false;
    if (a.hasAttribute('download') || a.getAttribute('rel') === 'nofollow') return false;
    // Match /products/... (Shopify may prefix a locale/market path, e.g. /en-nz/products/..)
    if (!/\/products\//.test(a.pathname)) return false;
    // Never prefetch a form / add-to-cart style URL.
    if (/[?&](add|variant=.*&.*add)/.test(a.search)) return false;
    return true;
  }

  // --- Preferred path: Speculation Rules API ------------------------------
  if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
    var rules = {
      prefetch: [
        {
          source: 'document',
          where: { href_matches: '/*/products/*' },
          eagerness: 'moderate'
        },
        {
          source: 'document',
          where: { href_matches: '/products/*' },
          eagerness: 'moderate'
        }
      ]
    };
    var s = document.createElement('script');
    s.type = 'speculationrules';
    s.textContent = JSON.stringify(rules);
    document.head.appendChild(s);
    return; // browser handles hover intent + prefetch for us
  }

  // --- Fallback: hover / touch / focus prefetch via <link rel=prefetch> ----
  var prefetched = Object.create(null);
  var supportsPrefetch = (function () {
    try {
      var link = document.createElement('link');
      return link.relList && link.relList.supports && link.relList.supports('prefetch');
    } catch (e) { return false; }
  })();
  if (!supportsPrefetch) return;

  function prefetch(url) {
    if (prefetched[url]) return;
    prefetched[url] = true;
    var l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = url;
    l.as = 'document';
    document.head.appendChild(l);
  }

  function maybePrefetchFrom(target) {
    var a = target && target.closest ? target.closest('a') : null;
    if (isProductLink(a)) prefetch(a.href);
  }

  var hoverTimer = null;
  document.addEventListener('mouseover', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!isProductLink(a)) return;
    // Small delay so a quick mouse pass-over doesn't trigger a fetch.
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { prefetch(a.href); }, 90);
  }, { passive: true });
  document.addEventListener('mouseout', function () { clearTimeout(hoverTimer); }, { passive: true });

  // Touch + keyboard: prefetch immediately on intent (no hover on touch).
  document.addEventListener('touchstart', function (e) { maybePrefetchFrom(e.target); }, { passive: true });
  document.addEventListener('focusin', function (e) { maybePrefetchFrom(e.target); }, { passive: true });
})();
